import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MissionForm } from "@/components/missions/mission-form"
import AppLayout from "@/layouts/app-layout"
import { missionsPath } from "@/routes"
import type { MissionsFormProps } from "@/types/missions"

/**
 * Brand/Missions/New — single-column form (max-w-720px) wrapping the
 * shared `<MissionForm>`. The page owns chrome (breadcrumb, title);
 * the form owns submit, validation, and conditional reveal.
 *
 * OKS-928 — deliberately does NOT pass `typeLocked`: an unsaved mission has
 * no collabs, so the type is always editable here and the prop's `false`
 * default is correct. Stated explicitly because "page omits the prop" is
 * exactly how the Edit bug looked, and a silent omission is
 * indistinguishable from a forgotten one.
 */
export default function MissionsNew({
  mission,
  brand_defaults,
  catalog_products,
  can_detach_product,
}: MissionsFormProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("missions.new.head_title")} />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
        <header className="flex flex-col gap-3">
          <Link
            href={missionsPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="missions-new-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("missions.new.breadcrumb_root")}
          </Link>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("missions.new.head_title")}
          </h1>
        </header>

        <MissionForm
          mission={mission}
          brandDefaults={brand_defaults}
          catalog={catalog_products}
          canDetachProduct={can_detach_product}
          action={missionsPath()}
          method="post"
        />
      </div>
    </AppLayout>
  )
}
